import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance1 from '../../Services/app/loginservice';
import { cleanCustomerId } from '../../Services/app/operatorservice';
import {
  Box, Typography, Paper, Chip, Button, FormControlLabel, Switch, Tooltip,
  CircularProgress, Divider,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';

// Maintenance Dashboard — the maintenance team's morning landing page.
// Reads Tool Life data only (per scoping decision); future expansions:
// alarms, preventive maintenance, work orders.
//
// Yantra palette:
//   orange brand  #ea580c   blue primary  #2563eb
//   surface       #f8fafc   card          #fff
//   ok teal       #0f766e   warning amber #ca8a04
//   alarm red     #b91c1c

const ATTR_KEY = 'interlock_config';

// ── DEMO DATA helpers (copied from Tool Dashboard for consistency) ────
const SAMPLE_TOOLS = [
  { tool_number: 1, name: 'Drill 6mm',              life_minutes: 180, life_parts: 500 },
  { tool_number: 2, name: 'Reamer 6.2mm',           life_minutes: 120, life_parts: 400 },
  { tool_number: 3, name: 'Roughing endmill 12mm',  life_minutes: 480, life_parts: 200 },
  { tool_number: 4, name: 'Finishing endmill 8mm',  life_minutes: 360, life_parts: 150 },
  { tool_number: 5, name: 'Chamfer 4mm',            life_minutes: 240, life_parts: 300 },
  { tool_number: 6, name: 'Boring bar 16mm',        life_minutes: 540, life_parts: 250 },
  { tool_number: 7, name: 'Threading tap M8',       life_minutes:  90, life_parts: 600 },
  { tool_number: 8, name: 'Face mill 50mm',         life_minutes: 600, life_parts: 180 },
];
const hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < (s || '').length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};
const demoMachineData = (machine) => {
  const h = hashStr(machine.name || 'x');
  const numTools = 3 + (h % 4);
  const tools = SAMPLE_TOOLS.slice(0, numTools).map(t => ({ ...t, enabled: true, warning_pct: 80, halt_pct: 100, halt_machine: true }));
  const activeTool = tools[h % tools.length].tool_number;
  const live = {};
  tools.forEach((t, i) => {
    const seed = (h + i * 97) >>> 0;
    const bucket = seed % 100;
    let pct;
    if (bucket < 6)        pct = 100 + (seed % 4);
    else if (bucket < 22)  pct = 80 + ((seed >> 3) % 19);
    else                   pct = ((seed >> 5) % 75);
    const minutes = Math.round((pct / 100) * t.life_minutes * 10) / 10;
    const parts = Math.round((pct / 100) * t.life_parts);
    live[t.tool_number] = { health_pct: pct, minutes, parts };
  });
  return { name: machine.name, tools, live, activeTool };
};
const demoRecentChanges = (devices) => {
  // Synthesize 10-15 recent tool changes for visual demo
  const events = [];
  const now = Date.now();
  const names = ['Suresh', 'Karthik', 'Ravi', 'Manoj', 'Yogesan', 'Vignesh'];
  devices.slice(0, 8).forEach((d, i) => {
    const h = hashStr(d.name);
    const numEvents = 1 + (h % 3);
    for (let k = 0; k < numEvents; k++) {
      const offset = ((h + k * 7919) % (24 * 3600 * 1000));
      const toolIdx = (h + k * 13) % SAMPLE_TOOLS.length;
      const t = SAMPLE_TOOLS[toolIdx];
      events.push({
        ts: now - offset,
        machine: d.name,
        tool_number: t.tool_number,
        tool_name: t.name,
        by: names[(h + k) % names.length],
        used_minutes: t.life_minutes - ((h + k) % 20),
        used_parts: t.life_parts - ((h + k) % 30),
      });
    }
  });
  events.sort((a, b) => b.ts - a.ts);
  return events.slice(0, 15);
};

const MaintenanceDashboard = () => {
  const navigate = useNavigate();
  const customerId = localStorage.getItem('CustomerID');
  const SERVER = (window._env_?.SERVER_URL || '').replace(/\/$/, '');
  const cid = cleanCustomerId(customerId);

  const [devices, setDevices] = useState([]);
  const [perMachine, setPerMachine] = useState({});
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const [demoMode, setDemoMode] = useState(false);
  const [demoExplicit, setDemoExplicit] = useState(false);
  const [firstRealLoadDone, setFirstRealLoadDone] = useState(false);

  // Load device list
  useEffect(() => {
    if (!cid) return;
    setLoading(true);
    axiosInstance1
      .get(`${SERVER}/api/customer/${cid}/devices`, { params: { pageSize: 500, page: 0 } })
      .then(r => {
        const list = (r.data?.data || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setDevices(list);
      })
      .catch(() => setDevices([]))
      .finally(() => setLoading(false));
  }, [SERVER, cid]);

  // Refresh real data
  const refresh = useCallback(async () => {
    if (!devices.length) return;
    setRefreshing(true);
    const next = {};
    const allEvents = [];
    await Promise.all(devices.map(async (d) => {
      const did = d.id?.id;
      try {
        const cfgR = await axiosInstance1.get(
          `${SERVER}/api/plugins/telemetry/DEVICE/${did}/values/attributes`,
          { params: { keys: ATTR_KEY } }
        );
        const cfg = (cfgR.data || []).find(a => a.key === ATTR_KEY)?.value || {};
        const tools = cfg.tool_life?.enabled ? (cfg.tool_life.tools || []).filter(t => t.enabled) : [];

        const keys = ['live_tool_number'];
        tools.forEach(t => {
          const n = String(t.tool_number).padStart(2, '0');
          keys.push(`tool_T${n}_health_pct`, `tool_T${n}_minutes`, `tool_T${n}_parts`);
        });
        let live = {};
        let activeTool = null;
        if (tools.length) {
          const liveR = await axiosInstance1.get(
            `${SERVER}/api/plugins/telemetry/DEVICE/${did}/values/timeseries`,
            { params: { keys: keys.join(','), useStrictDataTypes: false } }
          );
          const data = liveR.data || {};
          activeTool = data.live_tool_number?.[0]?.value;
          tools.forEach(t => {
            const n = String(t.tool_number).padStart(2, '0');
            live[t.tool_number] = {
              health_pct: Number(data[`tool_T${n}_health_pct`]?.[0]?.value ?? NaN),
              minutes: Number(data[`tool_T${n}_minutes`]?.[0]?.value ?? NaN),
              parts: Number(data[`tool_T${n}_parts`]?.[0]?.value ?? NaN),
            };
          });
        }
        next[did] = { name: d.name, tools, live, activeTool };

        // Recent change events for this machine (last 24h)
        const endTs = Date.now();
        const startTs = endTs - 24 * 3600 * 1000;
        try {
          const histR = await axiosInstance1.get(
            `${SERVER}/api/plugins/telemetry/DEVICE/${did}/values/timeseries`,
            { params: { keys: 'tool_change_log', startTs, endTs, limit: 50, useStrictDataTypes: false } }
          );
          const raw = histR.data?.tool_change_log || [];
          raw.forEach(e => {
            try {
              const obj = typeof e.value === 'string' ? JSON.parse(e.value) : e.value;
              if (obj) allEvents.push({ ts: e.ts, machine: d.name, ...obj });
            } catch {}
          });
        } catch {}
      } catch {
        next[did] = { name: d.name, tools: [], live: {}, error: true };
      }
    }));
    setPerMachine(next);
    allEvents.sort((a, b) => b.ts - a.ts);
    setRecentEvents(allEvents.slice(0, 20));
    setLastRefreshAt(Date.now());
    setRefreshing(false);

    if (!firstRealLoadDone) {
      const anyRealTools = Object.values(next).some(m => (m.tools || []).length > 0);
      if (!anyRealTools && !demoExplicit) setDemoMode(true);
      setFirstRealLoadDone(true);
    }
  }, [devices, SERVER, firstRealLoadDone, demoExplicit]);

  useEffect(() => { if (devices.length && !demoMode) refresh(); /* eslint-disable-line */ }, [devices, demoMode]);

  // Demo overlay
  useEffect(() => {
    if (!demoMode) return;
    const synth = {};
    devices.forEach(d => { synth[d.id?.id] = demoMachineData(d); });
    setPerMachine(synth);
    setRecentEvents(demoRecentChanges(devices));
    setLastRefreshAt(Date.now());
  }, [demoMode, devices]);

  // Derive rows
  const allRows = useMemo(() => {
    const rows = [];
    Object.entries(perMachine).forEach(([did, m]) => {
      (m.tools || []).forEach(t => {
        const live = m.live?.[t.tool_number] || {};
        const isActive = Number(m.activeTool) === Number(t.tool_number);
        const pct = Number(live.health_pct);
        const hasData = !isNaN(pct) && pct !== null;
        let status;
        if (!hasData) status = 'nodata';
        else if (pct >= (t.halt_pct ?? 100)) status = 'halt';
        else if (pct >= (t.warning_pct ?? 80)) status = 'warn';
        else status = 'ok';
        rows.push({ deviceId: did, machineName: m.name, tool: t, isActive, healthPct: hasData ? pct : null, status });
      });
    });
    return rows;
  }, [perMachine]);

  // KPIs
  const kpis = useMemo(() => {
    const haltRows = allRows.filter(r => r.status === 'halt');
    const machinesDown = new Set(haltRows.map(r => r.deviceId)).size;
    const toolsSoon = allRows.filter(r => r.status === 'warn').length;
    const toolsHealthy = allRows.filter(r => r.status === 'ok').length;
    const totalMonitored = allRows.length;
    return {
      machinesDown,
      toolsHalt: haltRows.length,
      toolsSoon,
      toolsHealthy,
      totalMonitored,
      recentCount: recentEvents.length,
    };
  }, [allRows, recentEvents]);

  const needsAttention = useMemo(() => {
    const halt = allRows.filter(r => r.status === 'halt');
    const warnTop = allRows
      .filter(r => r.status === 'warn')
      .sort((a, b) => (b.healthPct || 0) - (a.healthPct || 0))
      .slice(0, 5);
    return [...halt, ...warnTop];
  }, [allRows]);

  return (
    <Box sx={{ p: 3, bgcolor: '#f8fafc', minHeight: 'calc(100vh - 80px)' }}>
      {/* HEADER */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>
              🔧 Maintenance Dashboard
            </Typography>
            {demoMode && (
              <Chip
                label={demoExplicit ? 'DEMO DATA' : 'DEMO DATA (auto)'}
                size="small"
                sx={{ bgcolor: '#fed7aa', color: '#9a3412', fontWeight: 700, fontSize: 11 }}
              />
            )}
          </Box>
          <Typography sx={{ color: '#6b7280', fontSize: 13 }}>
            Start-of-shift command center for the maintenance team. Tools needing change, recent activity, quick links.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Tooltip title="Preview with synthetic data per machine. Turn off to see real (live) data.">
            <FormControlLabel
              control={<Switch checked={demoMode} onChange={(e) => { setDemoExplicit(true); setDemoMode(e.target.checked); }} sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#ea580c' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ea580c' },
              }} />}
              label={<Typography sx={{ fontSize: 12, color: demoMode ? '#9a3412' : 'inherit', fontWeight: demoMode ? 700 : 400 }}>🎨 Demo data</Typography>}
              sx={{ m: 0 }}
            />
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={14} /> : <Refresh fontSize="small" />}
            onClick={refresh}
            disabled={refreshing || demoMode}
            sx={{ textTransform: 'none', color: '#2563eb', borderColor: '#93c5fd' }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', bgcolor: 'var(--y-bg, #fff)' }}>
          <CircularProgress sx={{ color: '#ea580c' }} />
        </Paper>
      ) : (
        <>
          {/* KPI ROW */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5, mb: 2 }}>
            {[
              { num: kpis.machinesDown, label: 'MACHINES DOWN', color: '#b91c1c', bg: '#fef2f2', border: '#fecaca', click: () => navigate('/tool-dashboard') },
              { num: kpis.toolsHalt,    label: 'TOOLS AT HALT', color: '#b91c1c', bg: '#fef2f2', border: '#fecaca', click: () => navigate('/tool-dashboard') },
              { num: kpis.toolsSoon,    label: 'CHANGE SOON',   color: '#854d0e', bg: '#fef9c3', border: '#fde047', click: () => navigate('/tool-dashboard') },
              { num: kpis.recentCount,  label: 'CHANGES (24h)', color: '#1e40af', bg: '#dbeafe', border: '#93c5fd', click: () => {} },
              { num: kpis.toolsHealthy, label: 'TOOLS HEALTHY', color: '#065f46', bg: '#d1fae5', border: '#86efac', click: () => navigate('/tool-dashboard') },
            ].map((k, i) => (
              <Paper
                key={i}
                variant="outlined"
                onClick={k.click}
                sx={{
                  p: 2, bgcolor: k.bg, borderColor: k.border, cursor: k.click ? 'pointer' : 'default',
                  '&:hover': k.click ? { boxShadow: 2 } : {},
                }}
              >
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.num}</Typography>
                <Typography sx={{ fontSize: 10, color: k.color, fontWeight: 700, letterSpacing: 0.5, mt: 0.3 }}>{k.label}</Typography>
              </Paper>
            ))}
          </Box>

          {/* MAIN GRID: NEEDS ATTENTION + RECENT EVENTS */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 2, mb: 2 }}>
            {/* NEEDS ATTENTION */}
            <Paper variant="outlined" sx={{ bgcolor: 'var(--y-bg, #fff)' }}>
              <Box sx={{ p: 1.5, borderBottom: '1px solid var(--y-border, #e5e7eb)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#991b1b' }}>
                  🔴 Needs Attention Now ({needsAttention.length})
                </Typography>
                <Button size="small" sx={{ textTransform: 'none', color: '#2563eb', fontSize: 11 }} onClick={() => navigate('/tool-dashboard')}>
                  Open Tool Life Dashboard →
                </Button>
              </Box>
              <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {needsAttention.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    🎉 Nothing needs attention right now.
                  </Box>
                ) : (
                  needsAttention.map((r, i) => {
                    const isHalt = r.status === 'halt';
                    const c = isHalt ? '#b91c1c' : '#ca8a04';
                    const bg = isHalt ? '#fef2f2' : '#fefce8';
                    return (
                      <Paper
                        key={`${r.deviceId}-${r.tool.tool_number}-${i}`}
                        variant="outlined"
                        sx={{ px: 1.2, py: 0.6, bgcolor: bg, borderLeft: `3px solid ${c}`,
                              display: 'flex', alignItems: 'center', gap: 1.2 }}
                      >
                        <Chip size="small" label={`T${String(r.tool.tool_number).padStart(2, '0')}`}
                          sx={{ bgcolor: r.isActive ? '#ea580c' : '#2563eb', color: '#fff', fontWeight: 700, fontSize: 10, height: 18, minWidth: 36 }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1f2937', minWidth: 90 }}>
                          {r.machineName}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#374151', flex: 1, minWidth: 0,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.tool.name || '(unnamed)'}
                        </Typography>
                        {r.isActive && (
                          <Chip size="small" label="CUT" sx={{ bgcolor: '#fff7ed', color: '#9a3412', fontSize: 9, height: 14, fontWeight: 700 }} />
                        )}
                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: c, minWidth: 48, textAlign: 'right' }}>
                          {r.healthPct !== null ? `${r.healthPct.toFixed(0)}%` : '—'}
                        </Typography>
                      </Paper>
                    );
                  })
                )}
              </Box>
            </Paper>

            {/* RECENT EVENTS */}
            <Paper variant="outlined" sx={{ bgcolor: 'var(--y-bg, #fff)' }}>
              <Box sx={{ p: 1.5, borderBottom: '1px solid var(--y-border, #e5e7eb)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1e40af' }}>
                  📋 Recent Tool Changes (24h)
                </Typography>
                <Chip size="small" label={`${recentEvents.length}`} sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 700, fontSize: 10, height: 18 }} />
              </Box>
              <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 480, overflow: 'auto' }}>
                {recentEvents.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    No tool changes in the last 24 hours.
                  </Box>
                ) : (
                  recentEvents.map((e, i) => (
                    <Paper key={i} variant="outlined" sx={{ px: 1.2, py: 0.6, bgcolor: 'var(--y-bg, #fff)', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 10, color: '#6b7280', minWidth: 60, fontFamily: 'monospace' }}>
                        {new Date(e.ts).toLocaleTimeString().slice(0, 5)}
                      </Typography>
                      <Chip size="small" label={`T${String(e.tool_number ?? '?').padStart(2, '0')}`}
                        sx={{ bgcolor: '#2563eb', color: '#fff', fontWeight: 700, fontSize: 10, height: 16, minWidth: 30 }} />
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1f2937', minWidth: 80 }}>
                        {e.machine}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#374151', flex: 1, minWidth: 0,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.tool_name || e.name || '—'}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                        by {e.by || '—'}
                      </Typography>
                    </Paper>
                  ))
                )}
              </Box>
            </Paper>
          </Box>

          {/* QUICK LINKS */}
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'var(--y-bg, #fff)' }}>
            <Typography sx={{ fontSize: 12, color: '#6b7280', fontWeight: 700, mb: 1, letterSpacing: 0.5 }}>QUICK LINKS</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/tool-dashboard')}
                sx={{ textTransform: 'none', color: '#1f2937', borderColor: '#e5e7eb', '&:hover': { borderColor: '#ea580c', bgcolor: '#fff7ed' } }}
                startIcon={<Box sx={{ fontSize: 18 }}>🔧</Box>}
              >
                Tool Life Dashboard
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/interlock-settings')}
                sx={{ textTransform: 'none', color: '#1f2937', borderColor: '#e5e7eb', '&:hover': { borderColor: '#ea580c', bgcolor: '#fff7ed' } }}
                startIcon={<Box sx={{ fontSize: 18 }}>🛡</Box>}
              >
                Interlock Config
              </Button>
              <Tooltip title="Coming soon — preventive maintenance schedule">
                <span>
                  <Button
                    variant="outlined"
                    disabled
                    sx={{ textTransform: 'none' }}
                    startIcon={<Box sx={{ fontSize: 18 }}>📅</Box>}
                  >
                    PM Schedule
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Coming soon — alarm history & MTBF analysis">
                <span>
                  <Button
                    variant="outlined"
                    disabled
                    sx={{ textTransform: 'none' }}
                    startIcon={<Box sx={{ fontSize: 18 }}>🚨</Box>}
                  >
                    Alarm History
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Coming soon — spare parts inventory">
                <span>
                  <Button
                    variant="outlined"
                    disabled
                    sx={{ textTransform: 'none' }}
                    startIcon={<Box sx={{ fontSize: 18 }}>📦</Box>}
                  >
                    Spare Parts
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Paper>

          {lastRefreshAt && (
            <Typography sx={{ fontSize: 10, color: '#9ca3af', mt: 1.5, textAlign: 'right' }}>
              Last refreshed {new Date(lastRefreshAt).toLocaleTimeString()}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default MaintenanceDashboard;
